<template>
  <div class="chart">
    <VsLoader :visible="showLoader" />
    <template v-if="chartData">
      <TopChart
        :chartData="chartData"
        :options="chartOptions"
        :styles="{
          width: '95%',
          margin: 'auto',
        }"
      />
    </template>
  </div>
</template>
<script>
import TopChart from "./TopChart";
import tooltipConfig from "@/misc/chartTooltip";

export default {
  components: {
    TopChart,
  },
  props: {
    coverageData: {
      type: Object,
      required: true,
    },
  },
  data() {
    return {
      chartData: null,
    };
  },
  computed: {
    coverageRate() {
      return this.coverageData["vs-coverage-rate"];
    },
    pageViews() {
      return this.coverageData["user-saw-product"];
    },
    vsPageViews() {
      return this.coverageData["vs-views"];
    },
    showLoader() {
      return !!this.coverageRate.isLoading || !!this.pageViews.isLoading || !!this.vsPageViews.isLoading;
    },
    isChartAvailable() {
      if (!this.coverageRate.data || !this.pageViews.data || !this.vsPageViews.data) {
        return false;
      }
      return true;
    },
    chartOptions() {
      const numberFormat = this.$options.filters.numberFormat;
      return {
        maintainAspectRatio: false,
        legend: {
          display: false,
        },
        elements: { point: { radius: 0.05 } },
        scales: {
          xAxes: [
            {
              stacked: true,
              gridLines: {
                display: false,
              },
            },
          ],
          yAxes: [
            {
              stacked: true,
              position: "left",
              id: "line-axis",
              gridLines: {
                drawBorder: false,
                display: true,
              },
              ticks: {
                min: 0,
                max: 100,
                callback: (value) => `${value}  `,
              },
            },
            {
              stacked: true,
              position: "right",
              id: "bar-axis",
              gridLines: {
                drawBorder: false,
                display: false,
              },
              ticks: {
                callback: (value) => Number(value) / 1000 + "K",
              },
            },
          ],
        },
        tooltips: {
          ...tooltipConfig,
          callbacks: {
            title: function (tooltipItem, _data) {
              return `/${tooltipItem[0].label}`;
            },
            label: function (tooltipItem, data) {
              const dataset = data.datasets[tooltipItem.datasetIndex];
              return ` ${dataset.label}: `;
            },
            labelTextColor: function (tooltipItem, _chart) {
              return tooltipItem.datasetIndex === 1 ? "#EC386E" : "#16C6B9";
            },
            afterLabel: function (tooltipItem, data) {
              const dataset = data.datasets[tooltipItem.datasetIndex];
              const index = tooltipItem.index;
              const value = dataset.data[index];
              return `${numberFormat(value)}${dataset.name === "vsCoverageRate" ? "%" : ""}`;
            },
          },
        },
      };
    },
  },
  watch: {
    coverageData() {
      this.setChartData();
    },
    isChartAvailable() {
      this.setChartData();
    },
  },
  created() {
    this.setChartData();
  },
  methods: {
    async setChartData() {
      if (this.isChartAvailable) {
        this.chartData = await this.dataCollection();
      }
    },
    dataCollection() {
      const { DateString } = this.$options.filters;
      let coverageData = {
        labels: this.coverageRate.data.map((data) => DateString(data.date, "MM/DD")),
        datasets: [
          {
            type: "bar",
            label: this.$t("charts.itemCoverageChart.pageViewsWithVs"),
            name: "pageViewsWithVs",
            data: this.vsPageViews.data.map((data) => data.val),
            backgroundColor: "rgba(34,213,200, 0.2)",
            hoverBackgroundColor: "rgba(34,213,200, 0.76)",
            yAxisID: "bar-axis",
            borderColor: "rgba(34,213,200, 0.76)",
          },
          {
            type: "bar",
            label: this.$t("charts.itemCoverageChart.pageViewsWithoutVs"),
            name: "pageViewsWithoutVs",
            data: this.pageViews.data.map((data, index) => data.val - this.vsPageViews.data[index].val),
            backgroundColor: "rgba(244,98,142, 0.2)",
            hoverBackgroundColor: "rgba(244,98,142, 0.76)",
            yAxisID: "bar-axis",
            borderColor: "rgba(244,98,142, 0.76)",
          },
          {
            type: "line",
            label: this.$t("charts.itemCoverageChart.vsCoverageRate"),
            name: "vsCoverageRate",
            data: this.coverageRate.data.map((data) => (data.val ? Math.floor(data.val * 100) : null)),
            backgroundColor: "rgba(0, 0, 0, 0)",
            hoverBackgroundColor: "rgb(22,198,185)",
            borderColor: "rgb(22,198,185)",
            pointHoverBackgroundColor: "rgb(22,198,185)",
            yAxisID: "line-axis",
            borderWidth: 2,
            pointHoverRadius: 3,
          },
        ],
      };
      return Promise.resolve(coverageData);
    },
  },
};
</script>

<style lang="scss" scoped>
.chart {
  position: relative;
  min-height: 430px;
  padding: 15px 0;
  border-top: $border-light;
}
</style>
